import axiosClient from "utils/axios";
import { ApiOptionList, ApiResponse } from "../../../types/api.types";
import {
  CreateToolPayload,
  Tool,
  ToolsTableResponse,
  UpdateToolPayload,
} from "../types/tool.types";

function normalizeListResponse(
  raw: Tool[] | ToolsTableResponse,
  page: number,
  limit: number,
  search?: string,
  isActive?: boolean,
): ToolsTableResponse {
  let rows = Array.isArray(raw) ? raw : raw.rows;

  if (search?.trim()) {
    const q = search.trim().toLowerCase();
    rows = rows.filter((r) => r.name?.toLowerCase().includes(q));
  }

  if (isActive !== undefined) {
    rows = rows.filter((r) => r.isActive === isActive);
  }

  const totalItems = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));
  const start = (page - 1) * limit;
  const pageRows = rows.slice(start, start + limit);

  return {
    rows: pageRows,
    totalItems,
    currentPage: page,
    pageSize: limit,
    limit,
    totalPages,
  };
}

export const getTools = async ({
  page = 1,
  limit = 10,
  search,
  isActive,
}: {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
} = {}) => {
  try {
    const response = await axiosClient.get<
      ApiResponse<ToolsTableResponse | { rows: Tool[] }>
    >("/tools", {
      params: {
        query: search || undefined,
        page,
        limit,
        isActive,
      },
    });

    const payload = response.data.data;

    if (!payload) {
      const normalized = normalizeListResponse(
        [],
        page,
        limit,
        search,
        isActive,
      );
      return {
        success: response.data.success,
        message: response.data.message,
        data: normalized,
      } satisfies ApiResponse<ToolsTableResponse>;
    }

    if (
      payload &&
      typeof payload === "object" &&
      "totalItems" in payload &&
      Array.isArray(payload.rows)
    ) {
      return response.data as ApiResponse<ToolsTableResponse>;
    }

    const rows = (payload as { rows?: Tool[] })?.rows ?? [];
    const normalized = normalizeListResponse(
      rows,
      page,
      limit,
      search,
      isActive,
    );

    return {
      success: response.data.success,
      message: response.data.message,
      data: normalized,
    } satisfies ApiResponse<ToolsTableResponse>;
  } catch (err) {
    console.warn("Error fetching tools: ", err);
    throw err;
  }
};

export const getToolOptions = async ({
  query,
  limit = 20,
  page = 1,
}: {
  query?: string;
  limit?: number;
  page?: number;
}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiOptionList>>(
      "/tools/options",
      {
        params: {
          query,
          limit,
          page,
        },
      },
    );

    return response.data;
  } catch (err) {
    console.warn("Error fetching tool options: ", err);
    throw err;
  }
};

export const getTool = async (id: number) => {
  try {
    const response = await axiosClient.get<ApiResponse<Tool>>(
      `/tools/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching tool: ", err);
    throw err;
  }
};

export const createTool = async (payload: CreateToolPayload) => {
  try {
    const response = await axiosClient.post<ApiResponse<Tool>>(
      "/tools",
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating tool: ", err);
    throw err;
  }
};

export const updateTool = async (
  id: number,
  payload: UpdateToolPayload,
) => {
  try {
    const response = await axiosClient.put<ApiResponse<Tool>>(
      `/tools/${id}`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating tool: ", err);
    throw err;
  }
};

export const deleteTool = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<Tool>>(
      `/tools/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting tool: ", err);
    throw err;
  }
};
